import {
    type ContactsPermissionResponse,
    type Email,
    isAvailableAsync,
    PermissionStatus,
    type PhoneNumber,
    presentContactPickerAsync,
    requestPermissionsAsync,
} from "expo-contacts";
import { useEffect, useMemo, useState } from "react";
import { Button } from "~/components/Button";
import { Nothing } from "~/components/permission/Nothing";
import { useFeedback } from "~/providers/FeedbackProvider";
import { filled } from "~/util";

export type SelectFromContactsProps = {
    setEmail: (value: string) => void;
    setName: (value: string) => void;
    setPhone?: (value: string) => void;
    disabled?: boolean;
};

function extractEmail(emails?: Email[]): string {
    return (emails?.find(({ isPrimary }) => isPrimary) ?? emails?.[0])?.email ?? "";
}
function extractPhone(phones?: PhoneNumber[]): string {
    return (phones?.find(({ isPrimary }) => isPrimary) ?? phones?.[0])?.number ?? "";
}

export const SelectFromContacts = ({ setEmail, setName, setPhone, disabled }: SelectFromContactsProps) => {
    const [available, setAvailable] = useState(false);
    const [permission, setPermission] = useState<ContactsPermissionResponse | null>(null);
    const { setError } = useFeedback();

    useEffect(() => {
        isAvailableAsync().then(setAvailable);
        return () => setPermission(null);
    }, []);

    const trySelect = async () => {
        await requestPermissionsAsync().then(async (response) => {
            setPermission(response);

            if (response.granted) {
                await presentContactPickerAsync().then((selected) => {
                    if (selected) {
                        setName(selected.name);
                        setEmail(extractEmail(selected.emails));
                        setPhone?.(extractPhone(selected.phoneNumbers));
                    }
                });
            } else if (response.status === PermissionStatus.DENIED) {
                setError("You do not have permission to access contacts.");
            }
        });
    };

    const should_disable = useMemo(() => {
        return disabled || (filled(permission) && !permission.granted && !permission.canAskAgain);
    }, [disabled, permission]);

    return available ? (
        <Button icon="contact" variant="neutral-quiet" size="sm" disabled={should_disable} onPress={trySelect} />
    ) : (
        <Nothing />
    );
};
